import {
    Page,
    IndexTable,
    Card,
    Badge,
    Checkbox,
    ChoiceList,
    TextField,
    Autocomplete,
    Button,
    Thumbnail,
    IndexFilters,
    Text,
    Tooltip,
    useSetIndexFiltersMode,
    IndexFiltersMode,
    useIndexResourceState,
} from '@shopify/polaris';
import type { IndexFiltersProps } from '@shopify/polaris';
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../app/stores/store';
import agent from '../../app/api/agent';
import { ImageIcon, DeleteIcon } from '@shopify/polaris-icons';
import { getShopifyAppHandle, getShopifyStoreHandle } from '../../app/utils/shopifyAdmin';
import { truncateText } from '../../app/utils/truncateText';
import type { FitmentCatalogSettings } from '../../app/models/FitmentCatalog';
import {
    getFitmentFieldEmptyOptionLabel,
    getFitmentFieldValue,
    getLegacyFieldValues,
    getVisibleFitmentFields,
    hasAnyFitmentFieldValue,
    hasRequiredFitmentFieldValues,
    isFitmentFieldDisabled,
    normalizeFitmentFieldValues,
    normalizeFitmentKey,
    updateFitmentFieldValue,
    type FitmentFieldValues,
} from '../../app/utils/fitmentFields';

type SelectOption = { label: string; value: string };
type FitmentField = ReturnType<typeof getVisibleFitmentFields>[number];

const parseYearValue = (value: string): number | null => {
    const trimmed = value.trim();
    return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
};

const isStandardFitmentField = (field: FitmentField, key: string) =>
    (field.key || '').trim().toLowerCase() === key;

const orderFitmentFieldsForPlp = (fields: FitmentField[], yearField?: FitmentField) => {
    if (!yearField) {
        return fields;
    }

    const makeField = fields.find((field) => isStandardFitmentField(field, 'make'));
    const modelField = fields.find((field) => isStandardFitmentField(field, 'model'));
    const prioritizedFields = [makeField, modelField].filter((field): field is FitmentField => !!field);
    const prioritizedSet = new Set(prioritizedFields);

    return [
        ...prioritizedFields,
        ...fields.filter((field) => field !== yearField && !prioritizedSet.has(field)),
        yearField,
    ];
};

const getYearRangeValues = (startValue: string, endValue: string) => {
    const start = startValue.trim();
    const end = endValue.trim();

    if (!start && !end) {
        return { values: [''], error: '' };
    }

    if (!end || end === start) {
        return { values: [start], error: '' };
    }

    if (!start) {
        return { values: [], error: 'Choose a starting year before choosing Year To.' };
    }

    const startYear = parseYearValue(start);
    const endYear = parseYearValue(end);
    if (startYear == null || endYear == null) {
        return { values: [], error: 'Year ranges must use whole-number years.' };
    }

    if (startYear > endYear) {
        return { values: [], error: 'Year To must be the same as or after the starting year.' };
    }

    return {
        values: Array.from({ length: endYear - startYear + 1 }, (_, index) => `${startYear + index}`),
        error: '',
    };
};

const getAutocompleteOptions = (options: SelectOption[], value: string) => {
    const normalizedValue = value.trim().toLowerCase();
    const usableOptions = options.filter((option) => option.value);
    const matchingOptions = normalizedValue
        ? usableOptions.filter((option) =>
            option.label.toLowerCase().includes(normalizedValue)
            || option.value.toLowerCase().includes(normalizedValue))
        : usableOptions;

    return matchingOptions;
};

const FitmentValueAutocomplete = ({
    label,
    value,
    options,
    disabled,
    loading,
    error,
    placeholder,
    onChange,
}: {
    label: string;
    value: string;
    options: SelectOption[];
    disabled?: boolean;
    loading?: boolean;
    error?: string;
    placeholder?: string;
    onChange: (value: string) => void;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const filteredOptions = getAutocompleteOptions(options, value);

    useEffect(() => {
        containerRef.current
            ?.querySelectorAll<HTMLButtonElement>('.Polaris-TextField__ClearButton')
            .forEach((button) => {
                // Polaris clear buttons are focusable by default. Keeping them
                // out of tab order lets the combobox close before focus moves
                // to the next selector input.
                button.tabIndex = -1;
            });
    }, [value]);

    const textField = (
        <Autocomplete.TextField
            label={label}
            value={value}
            onChange={onChange}
            autoComplete="off"
            disabled={disabled}
            placeholder={placeholder}
            clearButton={!!value}
            onClearButtonClick={() => onChange('')}
            error={error ? true : undefined}
        />
    );

    return (
        <div ref={containerRef}>
            <Autocomplete
                options={filteredOptions}
                selected={value ? [value] : []}
                textField={textField}
                onSelect={(selected) => onChange(selected[0] ?? '')}
                loading={loading}
                emptyState={(
                    <div style={{ padding: 12, color: '#616161', fontSize: 13 }}>
                        No saved values. Type a new value to use it.
                    </div>
                )}
            />
        </div>
    );
};

const getSelectedFitmentRangeLabel = (
    fields: FitmentField[],
    values: FitmentFieldValues,
    yearField?: FitmentField,
    yearTo = '',
) =>
    fields
        .map((field) => {
            const value = getFitmentFieldValue(values, field);
            if (!value) {
                return '';
            }

            if (yearField && field === yearField && yearTo.trim() && yearTo.trim() !== value.trim()) {
                return `${value}-${yearTo.trim()}`;
            }

            return value;
        })
        .filter(Boolean)
        .join(' ');


declare global {
    interface Window {
        shopify?: {
            idToken?: () => Promise<string>;
            toast?: { show?: (msg: string | { message: string; duration?: number }) => void };
        };
    }
}

const showToast = (msg: string) => window.shopify?.toast?.show?.(msg);

const PRODUCT_TITLE_DISPLAY_LIMIT = 80;

const ProductsPage: React.FC = () => {
    const navigate = useNavigate();
    const { productStore, userStore } = useStore();
    const { products, loadProducts, page, hasNext, totalCount, mappedCount } = productStore;
    const [fitmentCatalog, setFitmentCatalog] = useState<FitmentCatalogSettings | null>(null);
    const selectorFields = useMemo(() => getVisibleFitmentFields(fitmentCatalog), [fitmentCatalog]);
    const standardYearField = useMemo(
        () => selectorFields.find((field) => isStandardFitmentField(field, 'year')),
        [selectorFields],
    );
    const productListSelectorFields = useMemo(
        () => orderFitmentFieldsForPlp(selectorFields, standardYearField),
        [selectorFields, standardYearField],
    );
    const selectorFieldKey = useMemo(
        () => selectorFields.map((field) => normalizeFitmentKey(field)).join('|'),
        [selectorFields],
    );
    const [selectedFitmentValues, setSelectedFitmentValues] = useState<FitmentFieldValues>({});
    const [selectedFitmentYearTo, setSelectedFitmentYearTo] = useState('');
    const selectedLegacyValues = getLegacyFieldValues(selectedFitmentValues);
    const selectedYear = selectedLegacyValues.year;
    const selectedYearRange = useMemo(
        () => standardYearField
            ? getYearRangeValues(selectedYear, selectedFitmentYearTo)
            : { values: [''], error: '' },
        [selectedFitmentYearTo, selectedYear, standardYearField],
    );
    const selectedYearToForRequest = standardYearField && !selectedYearRange.error
        ? selectedFitmentYearTo.trim()
        : '';
    const selectedMake = selectedLegacyValues.make;
    const selectedModel = selectedLegacyValues.model;

    const [queryValue, setQueryValue] = useState<string>('');
    const handleQueryChange = useCallback((value: string) => setQueryValue(value), []);
    const handleQueryClear = useCallback(() => setQueryValue(''), []);

    const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Filtering);
    const onHandleCancel = () => { };

    const [hasFitment, setHasFitment] = useState<string[]>([]);
    const handleHasFitmentChange = useCallback((value: string[]) => setHasFitment(value), []);
    const handleHasFitmentRemove = useCallback(() => setHasFitment([]), []);

    const [vendor, setVendor] = useState<string>('');
    const handleVendorChange = useCallback((value: string) => setVendor(value), []);
    const handleVendorRemove = useCallback(() => setVendor(''), []);

    const [taggedWith, setTaggedWith] = useState<string>('');
    const handleTaggedWithChange = useCallback((value: string) => setTaggedWith(value), []);
    const handleTaggedWithRemove = useCallback(() => setTaggedWith(''), []);

    const queryDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const skipInitialQueryDebounceRef = useRef(true);
    const queryValueRef = useRef(queryValue);

    // Table overlay state
    const [isPaging, setIsPaging] = useState(false);

    const filters = [
        {
            key: 'hasFitment',
            label: 'Has fitments',
            filter: (
                <ChoiceList
                    title="Has fitments"
                    titleHidden
                    choices={[
                        { label: 'Has fitments', value: 'has' },
                        { label: 'No fitments', value: 'none' },
                    ]}
                    selected={hasFitment}
                    onChange={handleHasFitmentChange}
                />
            ),
            shortcut: true,
        },
        {
            key: 'vendor',
            label: 'Vendor',
            filter: (
                <TextField
                    label="Vendor"
                    value={vendor}
                    onChange={handleVendorChange}
                    autoComplete="off"
                    labelHidden
                />
            ),
            shortcut: true,
        },
        {
            key: 'taggedWith',
            label: 'Tag',
            filter: (
                <TextField
                    label="Tag"
                    value={taggedWith}
                    onChange={handleTaggedWithChange}
                    autoComplete="off"
                    labelHidden
                />
            ),
            shortcut: true,
        },
    ];

    const appliedFilters: IndexFiltersProps['appliedFilters'] = [];
    if (hasFitment.length > 0) {
        appliedFilters.push({
            key: 'hasFitment',
            label: hasFitment[0] === 'has' ? 'Has fitments' : 'No fitments',
            onRemove: handleHasFitmentRemove,
        });
    }
    if (!isEmpty(vendor)) {
        appliedFilters.push({
            key: 'vendor',
            label: `Vendor is ${vendor}`,
            onRemove: handleVendorRemove,
        });
    }
    if (!isEmpty(taggedWith)) {
        appliedFilters.push({
            key: 'taggedWith',
            label: `Tagged with ${taggedWith}`,
            onRemove: handleTaggedWithRemove,
        });
    }

    const handleFiltersClearAll = useCallback(() => {
        setHasFitment([]);
        setVendor('');
        setTaggedWith('');
        handleQueryClear();
    }, [handleQueryClear]);

    const [mappedOnly, setMappedOnly] = useState(false);
    const [fieldOptions, setFieldOptions] = useState<Record<string, SelectOption[]>>({});
    const [loadingFieldOptions, setLoadingFieldOptions] = useState(false);

    // A product can be mapped once the merchant's required selector fields are
    // filled. If the catalog has no required fields, any selected value is a
    // valid context for broad/custom catalogs.
    const hasVehicleContext = Boolean(
        productListSelectorFields.length > 0
        && !selectedYearRange.error
        && (productListSelectorFields.some((field) => field.isRequired)
            ? hasRequiredFitmentFieldValues(productListSelectorFields, selectedFitmentValues)
            : hasAnyFitmentFieldValue(productListSelectorFields, selectedFitmentValues))
    );

    // Normalize ids to strings for Polaris
    const resourceProducts = products.map((p) => ({ ...p, id: String(p.id) }));

    const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
        useIndexResourceState(resourceProducts);

    const onSelectionChange: NonNullable<
        React.ComponentProps<typeof IndexTable>['onSelectionChange']
    > = useCallback(
        (selectionType, toggleType, selection, position) => {
            handleSelectionChange(selectionType, toggleType, selection, position);

            const pageCount = resourceProducts.length;
            const selectedCount = selectedResources.length;

            const shouldClear =
                (selectionType === 'all' &&
                    toggleType === false &&
                    (allResourcesSelected || selectedCount >= totalCount)) ||
                (selectionType !== 'all' && selectedCount >= pageCount);

            if (shouldClear) clearSelection();
        },
        [
            handleSelectionChange,
            clearSelection,
            selectedResources.length,
            resourceProducts.length,
            allResourcesSelected,
            totalCount,
        ]
    );

    const isCurrentPageFullySelected =
        !allResourcesSelected &&
        resourceProducts.length > 0 &&
        selectedResources.length === resourceProducts.length;

    const paginatedSelectAllText = allResourcesSelected
        ? `All ${totalCount} products in your store are selected.`
        : isCurrentPageFullySelected
            ? `All ${resourceProducts.length} on this page are selected.`
            : undefined;

    const paginatedSelectAllActionText =
        !allResourcesSelected && isCurrentPageFullySelected ? `Select all ${totalCount} products` : undefined;

    const sortOptions: IndexFiltersProps['sortOptions'] = [
        { label: 'Product title', value: 'title asc', directionLabel: 'A-Z' },
        { label: 'Product title', value: 'title desc', directionLabel: 'Z-A' },
        { label: 'Fitments', value: 'fitments asc', directionLabel: 'Lowest to highest' },
        { label: 'Fitments', value: 'fitments desc', directionLabel: 'Highest to lowest' },
        { label: 'Created', value: 'created asc', directionLabel: 'Oldest first' },
        { label: 'Created', value: 'created desc', directionLabel: 'Newest first' },
        { label: 'Updated', value: 'updated asc', directionLabel: 'Oldest first' },
        { label: 'Updated', value: 'updated desc', directionLabel: 'Newest first' },
    ];
    const [sortSelected, setSortSelected] = useState(['title asc']);

    const selectedCount = allResourcesSelected ? totalCount : selectedResources.length;
    const selectedVehicleLabel = getSelectedFitmentRangeLabel(
        productListSelectorFields,
        selectedFitmentValues,
        standardYearField,
        selectedYearToForRequest,
    );

    const onBulkFitmentAction = useCallback(async (action: 'add' | 'delete') => {
        if (selectedCount < 1) return;

        const requiresVehicleSelection = action === 'add';
        if (requiresVehicleSelection && !hasVehicleContext) {
            const requiredLabels = productListSelectorFields
                .filter((field) => field.isRequired)
                .map((field) => field.label.toLowerCase());
            showToast(requiredLabels.length > 0
                ? `Select ${requiredLabels.join(', ')} before running this action`
                : 'Select at least one selector value before running this action');
            return;
        }

        try {
            // Send both the legacy Y/M/M values and the full fieldValues map.
            // Legacy values keep old endpoints/tags compatible; fieldValues is
            // the source of truth for 4-8 fields and non-automotive catalogs.
            await agent.Products.bulkFitments({
                action,
                productIds: allResourcesSelected ? null : selectedResources.map((id) => Number(id)),
                scope: allResourcesSelected
                    ? {
                        query: queryValue,
                        hasFitment: hasFitment[0] || '',
                        vendor,
                        tag: taggedWith,
                    }
                    : undefined,
                year: selectedYear,
                yearTo: selectedYearToForRequest,
                make: selectedMake,
                model: selectedModel,
                submodel: selectedLegacyValues.submodel,
                fieldValues: normalizeFitmentFieldValues(selectorFields, selectedFitmentValues),
            });

            showToast(
                action === 'add'
                    ? (hasVehicleContext ? `Mapped to ${selectedVehicleLabel}` : 'Fitments added')
                    : (hasVehicleContext ? `Unmapped from ${selectedVehicleLabel}` : 'Fitments removed')
            );

            clearSelection();
            await loadProducts(page, queryValue, hasFitment[0] || '', vendor, taggedWith, sortSelected[0], selectedYear, selectedYearToForRequest, selectedMake, selectedModel, mappedOnly, normalizeFitmentFieldValues(selectorFields, selectedFitmentValues));
        } catch (e: any) {
            showToast(e?.message ?? 'Failed to update fitments');
        }
    }, [
        selectedCount,
        selectedYear,
        selectedYearToForRequest,
        selectedMake,
        selectedModel,
        selectedLegacyValues.submodel,
        hasVehicleContext,
        selectorFields,
        productListSelectorFields,
        selectedFitmentValues,
        selectedVehicleLabel,
        allResourcesSelected,
        selectedResources,
        queryValue,
        hasFitment,
        vendor,
        taggedWith,
        clearSelection,
        loadProducts,
        page,
        sortSelected,
        mappedOnly,
    ]);

    const promotedBulkActions = [
        {
            content: hasVehicleContext && selectedVehicleLabel
                ? `Map to ${selectedVehicleLabel}`
                : 'Add fitments',
            onAction: () => onBulkFitmentAction('add'),
            disabled: selectedCount < 1 || !hasVehicleContext,
        },
    ];
    const bulkActions = [
        {
            icon: DeleteIcon,
            content: hasVehicleContext && selectedVehicleLabel
                ? `Unmap from ${selectedVehicleLabel}`
                : 'Remove all fitments',
            destructive: true,
            onAction: () => onBulkFitmentAction('delete'),
            disabled: selectedCount < 1,
        },
    ];

    useEffect(() => {
        queryValueRef.current = queryValue;
    }, [queryValue]);

    useEffect(() => {
        let cancelled = false;

        const loadFitmentCatalog = async () => {
            try {
                const catalog = await agent.Settings.fitmentCatalog();
                if (!cancelled) setFitmentCatalog(catalog);
            } catch {
                if (!cancelled) setFitmentCatalog(null);
            }
        };

        loadFitmentCatalog();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        setSelectedFitmentValues((current) => normalizeFitmentFieldValues(selectorFields, current));
    }, [selectorFieldKey, selectorFields]);

    useEffect(() => {
        if (!standardYearField) {
            setSelectedFitmentYearTo('');
        }
    }, [standardYearField]);

    useEffect(() => {
        let cancelled = false;

        const loadOptions = async () => {
            if (productListSelectorFields.length === 0) {
                setFieldOptions({});
                return;
            }

            setLoadingFieldOptions(true);
            try {
                // The options endpoint returns one list per selector key. That
                // lets this page render Year/Make/Model, YMMS, or custom fields
                // without adding page-specific dropdown logic.
                const normalizedValues = normalizeFitmentFieldValues(productListSelectorFields, selectedFitmentValues);
                const result = await agent.Vehicles.filterOptions(
                    normalizedValues.year || '',
                    normalizedValues.make || '',
                    '',
                    normalizedValues,
                    productListSelectorFields.map((field) => normalizeFitmentKey(field)),
                );

                if (cancelled) return;

                const nextOptions = productListSelectorFields.reduce<Record<string, SelectOption[]>>((accumulator, field) => {
                    const key = normalizeFitmentKey(field);
                    const values = result.optionsByField?.[key] ?? [];
                    accumulator[key] = [
                        { label: getFitmentFieldEmptyOptionLabel(field, 'all'), value: '' },
                        ...values.map((value) => ({ label: value, value })),
                    ];
                    return accumulator;
                }, {});

                setFieldOptions(nextOptions);
            } catch (error) {
                console.log(error);
                if (!cancelled) {
                    setFieldOptions({});
                }
            } finally {
                if (!cancelled) {
                    setLoadingFieldOptions(false);
                }
            }
        };

        loadOptions();
        return () => {
            cancelled = true;
        };
    }, [productListSelectorFields, selectedFitmentValues]);

    // Load data immediately for non-query changes (filters/sort).
    useEffect(() => {
        if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current);
        productStore.loadProducts(1, queryValueRef.current, hasFitment[0] || '', vendor, taggedWith, sortSelected[0], selectedYear, selectedYearToForRequest, selectedMake, selectedModel, mappedOnly, normalizeFitmentFieldValues(selectorFields, selectedFitmentValues));
    }, [productStore, hasFitment, vendor, taggedWith, sortSelected, selectedYear, selectedYearToForRequest, selectedMake, selectedModel, mappedOnly, selectorFields, selectedFitmentValues]);

    // Keep debounce only for free-text search changes.
    useEffect(() => {
        if (skipInitialQueryDebounceRef.current) {
            skipInitialQueryDebounceRef.current = false;
            return;
        }

        if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current);

        queryDebounceRef.current = setTimeout(() => {
            productStore.loadProducts(1, queryValue, hasFitment[0] || '', vendor, taggedWith, sortSelected[0], selectedYear, selectedYearToForRequest, selectedMake, selectedModel, mappedOnly, normalizeFitmentFieldValues(selectorFields, selectedFitmentValues));
        }, 500);

        return () => {
            if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current);
        };
    }, [productStore, queryValue, hasFitment, vendor, taggedWith, sortSelected, selectedYear, selectedYearToForRequest, selectedMake, selectedModel, mappedOnly, selectorFields, selectedFitmentValues]);

    // One handler for next/prev that scrolls + overlays table
    const runPagedLoad = useCallback(
        async (targetPage: number) => {
            // Scroll immediately (smooth)
            window.scrollTo({ top: 0, behavior: 'smooth' });

            // Overlay only the table area while loading
            setIsPaging(true);
            try {
                await Promise.resolve(
                    loadProducts(targetPage, queryValue, hasFitment[0] || '', vendor, taggedWith, sortSelected[0], selectedYear, selectedYearToForRequest, selectedMake, selectedModel, mappedOnly, normalizeFitmentFieldValues(selectorFields, selectedFitmentValues))
                );
            } finally {
                setIsPaging(false);
            }
        },
        [loadProducts, queryValue, hasFitment, vendor, taggedWith, sortSelected, selectedYear, selectedYearToForRequest, selectedMake, selectedModel, mappedOnly, selectorFields, selectedFitmentValues]
    );

    const tableIsBusy = isPaging || productStore.loadingInitial;

    const shopHandle = getShopifyStoreHandle(userStore.user?.shopDomain);
    const appHandle = getShopifyAppHandle();

    const buildProductAdminUrl = useCallback((productId: string) => {
        if (!shopHandle || !appHandle) {
            return `/products/${productId}`;
        }

        return `https://admin.shopify.com/store/${shopHandle}/apps/${appHandle}/products/${productId}`;
    }, [appHandle, shopHandle]);

    const handleTitleLinkClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>, productId: string) => {
        event.stopPropagation();

        if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return;
        }

        event.preventDefault();
        navigate(`/products/${productId}`);
    }, [navigate]);

    const handleTableClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        const clickTarget = event.target as HTMLElement;
        const clickedCheckboxColumn = clickTarget.closest('td:first-child');

        if (!clickedCheckboxColumn) {
            return;
        }

        const checkboxElement = clickedCheckboxColumn.querySelector<HTMLInputElement>('input[type="checkbox"]');
        if (!checkboxElement || clickTarget === checkboxElement) {
            return;
        }

        event.stopPropagation();
        event.preventDefault();
        checkboxElement.click();
    }, []);

    return (
        <Page fullWidth title="Products">
            <Card>
                <div className="pf-fitment-selector">
                    {productListSelectorFields.map((field) => {
                        const key = normalizeFitmentKey(field);
                        const isYearField = field === standardYearField;
                        const fieldDisabled = isFitmentFieldDisabled(productListSelectorFields, selectedFitmentValues, field, false);
                        return (
                            <React.Fragment key={key}>
                                <div className="pf-fitment-selector__field">
                                    <FitmentValueAutocomplete
                                        label={field.label}
                                        options={fieldOptions[key] ?? [{ label: getFitmentFieldEmptyOptionLabel(field, 'all'), value: '' }]}
                                        value={getFitmentFieldValue(selectedFitmentValues, field)}
                                        disabled={fieldDisabled}
                                        loading={loadingFieldOptions}
                                        placeholder={getFitmentFieldEmptyOptionLabel(field, 'all')}
                                        onChange={(value) => {
                                            setSelectedFitmentValues((current) => updateFitmentFieldValue(productListSelectorFields, current, field, value));
                                            setMappedOnly(false);
                                        }}
                                    />
                                </div>
                                {isYearField && (
                                    <div className="pf-fitment-selector__field">
                                        <FitmentValueAutocomplete
                                            label="Year To"
                                            options={fieldOptions[key] ?? []}
                                            value={selectedFitmentYearTo}
                                            disabled={fieldDisabled || !selectedYear}
                                            loading={loadingFieldOptions}
                                            placeholder="Select year to"
                                            error={selectedYearRange.error}
                                            onChange={(value) => {
                                                setSelectedFitmentYearTo(value);
                                                setMappedOnly(false);
                                            }}
                                        />
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                    {productListSelectorFields.length > 0 && (
                        <div className="pf-fitment-selector__action">
                            <Button
                                onClick={() => {
                                    setSelectedFitmentValues({});
                                    setSelectedFitmentYearTo('');
                                    setMappedOnly(false);
                                }}
                                disabled={!hasAnyFitmentFieldValue(productListSelectorFields, selectedFitmentValues) && !selectedFitmentYearTo}
                            >
                                Clear
                            </Button>
                        </div>
                    )}
                </div>


                {hasVehicleContext && (
                    <div className="pf-products-vehicle-context">
                        <div className="pf-products-vehicle-context__details">
                            <Text as="p" variant="bodySm" tone="subdued">
                                Selected vehicle
                            </Text>
                            <div className="pf-products-vehicle-context__vehicle">
                                <Text as="p" variant="bodyMd" fontWeight="semibold">
                                    {selectedVehicleLabel}
                                </Text>
                                <Badge tone={mappedCount === 0 ? 'critical' : undefined}>
                                    {`${mappedCount.toLocaleString()} mapped product${mappedCount === 1 ? '' : 's'}`}
                                </Badge>
                            </div>
                        </div>
                        <div className="pf-products-vehicle-context__filter">
                            <Checkbox
                                label="Show mapped only"
                                checked={mappedOnly}
                                onChange={setMappedOnly}
                            />
                        </div>
                    </div>
                )}
                {!hasVehicleContext && (
                    <div style={{ margin: '0 10px 16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#8a8a8a' }}>
                            <input type="checkbox" checked={false} disabled />
                            Show mapped only
                        </label>
                    </div>
                )}

                <IndexFilters
                    sortOptions={sortOptions}
                    sortSelected={sortSelected}
                    onSort={setSortSelected}
                    loading={productStore.loadingInitial}
                    cancelAction={{ onAction: onHandleCancel, disabled: false, loading: false }}
                    tabs={[]}
                    selected={0}
                    canCreateNewView={false}
                    queryValue={queryValue}
                    queryPlaceholder="Search products"
                    onQueryChange={handleQueryChange}
                    onQueryClear={handleQueryClear}
                    onClearAll={handleFiltersClearAll}
                    filters={filters}
                    appliedFilters={appliedFilters}
                    mode={mode}
                    setMode={setMode}
                />

                {/* Wrap the table so we can overlay ONLY this area */}
                <div style={{ position: 'relative' }}>
                    {tableIsBusy && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(0,0,0,0.05)',
                                zIndex: 10,
                                pointerEvents: 'auto',
                            }}
                        />
                    )}

                    <div style={{ pointerEvents: tableIsBusy ? 'none' : 'auto' }} onClickCapture={handleTableClickCapture}>
                        <IndexTable
                            bulkActions={bulkActions}
                            promotedBulkActions={promotedBulkActions}
                            resourceName={{ singular: 'product', plural: 'products' }}
                            itemCount={totalCount}
                            hasMoreItems={totalCount > resourceProducts.length}
                            selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                            onSelectionChange={onSelectionChange}
                            paginatedSelectAllText={paginatedSelectAllText}
                            paginatedSelectAllActionText={paginatedSelectAllActionText}
                            headings={[
                                { title: 'Product' },
                                { title: 'Source' },
                                { title: 'SKU' },
                                { title: 'Fitments' },
                                { title: '' },
                            ]}
                            pagination={{
                                hasNext: hasNext && !tableIsBusy,
                                hasPrevious: page > 1 && !tableIsBusy,
                                onNext: () => runPagedLoad(page + 1),
                                onPrevious: () => runPagedLoad(page - 1),
                            }}
                        >
                            {resourceProducts.map((product, index) => {
                                const displayTitle = truncateText(product.title, PRODUCT_TITLE_DISPLAY_LIMIT);
                                const showTitleTooltip = product.title.length > PRODUCT_TITLE_DISPLAY_LIMIT;
                                const titleLink = (
                                    <a
                                        href={buildProductAdminUrl(product.id)}
                                        onClick={(event) => handleTitleLinkClick(event, product.id)}
                                        style={{ color: 'inherit', textDecoration: 'none' }}
                                        onMouseEnter={(event) => { event.currentTarget.style.textDecoration = 'underline'; }}
                                        onMouseLeave={(event) => { event.currentTarget.style.textDecoration = 'none'; }}
                                        aria-label={product.title}
                                    >
                                        {displayTitle}
                                    </a>
                                );

                                return (
                                    <IndexTable.Row
                                        id={product.id}
                                        key={product.id}
                                        position={index}
                                        selected={allResourcesSelected ? true : selectedResources.includes(product.id)}
                                        onClick={() => navigate(`/products/${product.id}`)}
                                    >
                                        <IndexTable.Cell>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderLeft: product.isMappedToSelectedVehicle ? '3px solid #2f855a' : '3px solid transparent', paddingLeft: 8 }}>
                                                {product.featuredImage ? (
                                                    <Thumbnail source={product.featuredImage} alt={product.title} size="small" />
                                                ) : (
                                                    <Thumbnail source={ImageIcon} alt={product.title} size="small" />
                                                )}
                                                <span style={{ display: 'inline-flex' }}>
                                                    {showTitleTooltip ? (
                                                        <Tooltip
                                                            content={product.title}
                                                            preferredPosition="below"
                                                            width="wide"
                                                            accessibilityLabel={product.title}
                                                        >
                                                            {titleLink}
                                                        </Tooltip>
                                                    ) : titleLink}
                                                </span>
                                            </div>
                                        </IndexTable.Cell>
                                        <IndexTable.Cell>{product.isAtdProduct ? 'ATD' : 'Shopify'}</IndexTable.Cell>
                                        <IndexTable.Cell>{product.sku}</IndexTable.Cell>
                                        <IndexTable.Cell>{product.fitmentCount}</IndexTable.Cell>
                                        <IndexTable.Cell />
                                    </IndexTable.Row>
                                );
                            })}
                        </IndexTable>
                    </div>
                </div>
            </Card>
        </Page>
    );
};

function isEmpty(value: string | string[]): boolean {
    if (Array.isArray(value)) return value.length === 0;
    return value === '' || value == null;
}

export default observer(ProductsPage);
